var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { random } from '@hyper-hyper-space/hhs3_crypto';
import { json } from '@hyper-hyper-space/hhs3_json';
import { stringToUint8Array } from '@hyper-hyper-space/hhs3_crypto';
const MAX_HEADERS_PER_REQUEST = 1024;
const MAX_PAYLOAD_REQUESTS_PER_PEER = 2;
const PAYLOAD_CHUNK_SIZE = 64;
const REQUEST_TIMEOUT_MS = 30000;
/** Set `HHS3_SYNC_FRONTIER_LOG=1` to log new-frontier gossip (dag id + hash prefixes). */
function frontierLogEnabled() {
    var _a;
    try {
        const proc = globalThis.process;
        return ((_a = proc === null || proc === void 0 ? void 0 : proc.env) === null || _a === void 0 ? void 0 : _a['HHS3_SYNC_FRONTIER_LOG']) === '1';
    }
    catch (_b) {
        return false;
    }
}
function shortHash(h) {
    return h.length <= 16 ? h : `${h.slice(0, 12)}…`;
}
function frontierShort(frontier) {
    return [...frontier].map(shortHash);
}
export function createDagSynchronizer(dagId, dag, rObject, hashSuite, getPeers, sendTo, resolveRefDag) {
    // --- accumulative state ---
    const peerFrontiers = new Map();
    const peerDiscoveredFrontier = new Map();
    const discoveredHeaders = new Map();
    const readyToApply = new Map();
    const requestedPayloads = new Set();
    const receivedPayloads = new Map();
    const appliedEntries = new Set();
    const hashSourcePeer = new Map();
    const pendingHeaderRequests = new Map();
    const pendingPayloadRequests = new Map();
    const lastSentFrontier = new Map();
    const PUSHBACK_INTERVAL_MS = 15000;
    const issueCallbacks = [];
    function reportIssue(peerKey, issue) {
        for (const cb of issueCallbacks)
            cb(peerKey, issue);
    }
    let autoPayloadRequestId;
    let destroyed = false;
    const onGrowth = () => {
        if (destroyed)
            return;
        broadcastFrontier();
        attemptWork();
    };
    dag.addListener(onGrowth);
    let workInProgress = false;
    let workNeeded = false;
    // --- request ID generation ---
    function newRequestId() {
        const bytes = random.getBytes(8);
        return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    }
    // --- gossip ---
    function broadcastFrontier() {
        return __awaiter(this, void 0, void 0, function* () {
            const frontier = yield dag.getFrontier();
            const msg = {
                type: 'new-frontier',
                dagId,
                frontier: [...frontier],
            };
            if (frontierLogEnabled()) {
                const peers = getPeers();
                console.debug('[hhs3:sync:frontier] broadcast', {
                    dagId: shortHash(dagId),
                    frontier: frontierShort(frontier),
                    peerCount: peers.length,
                    peerKeys: peers.map(p => p.key),
                });
            }
            const now = Date.now();
            for (const peer of getPeers()) {
                sendTo(peer, msg);
                if (peerFrontiers.has(peer.key)) {
                    lastSentFrontier.set(peer.key, { frontier: new Set(frontier), timestamp: now });
                }
            }
        });
    }
    function sendFrontierTo(peer) {
        return __awaiter(this, void 0, void 0, function* () {
            const frontier = yield dag.getFrontier();
            const msg = {
                type: 'new-frontier',
                dagId,
                frontier: [...frontier],
            };
            if (frontierLogEnabled()) {
                console.debug('[hhs3:sync:frontier] push-back send', {
                    dagId: shortHash(dagId),
                    toPeer: peer.key,
                    frontier: frontierShort(frontier),
                });
            }
            sendTo(peer, msg);
            lastSentFrontier.set(peer.key, { frontier: new Set(frontier), timestamp: Date.now() });
        });
    }
    function handleNewFrontier(msg, peerKey) {
        return __awaiter(this, void 0, void 0, function* () {
            if (frontierLogEnabled()) {
                console.debug('[hhs3:sync:frontier] recv', {
                    dagId: shortHash(msg.dagId),
                    fromPeer: peerKey,
                    remoteFrontier: frontierShort(msg.frontier),
                });
            }
            peerFrontiers.set(peerKey, new Set(msg.frontier));
            for (const h of msg.frontier) {
                if (discoveredHeaders.has(h) || readyToApply.has(h) || appliedEntries.has(h)) {
                    addToPeerDiscoveredFrontier(peerKey, h);
                }
            }
            yield attemptWork();
            // Push-back: send our frontier so the peer can discover it is behind
            // (or in a fork). Suppress if frontiers are identical, or if we already
            // sent the same frontier to this peer recently (within PUSHBACK_INTERVAL_MS).
            const localFrontier = yield dag.getFrontier();
            const remoteFrontier = new Set(msg.frontier);
            if (frontiersEqual(localFrontier, remoteFrontier)) {
                if (frontierLogEnabled()) {
                    console.debug('[hhs3:sync:frontier] push-back skip (identical)', {
                        dagId: shortHash(dagId),
                        fromPeer: peerKey,
                    });
                }
                return;
            }
            const prev = lastSentFrontier.get(peerKey);
            if (prev !== undefined
                && frontiersEqual(prev.frontier, localFrontier)
                && Date.now() - prev.timestamp < PUSHBACK_INTERVAL_MS) {
                if (frontierLogEnabled()) {
                    console.debug('[hhs3:sync:frontier] push-back skip (rate-limited)', {
                        dagId: shortHash(dagId),
                        fromPeer: peerKey,
                    });
                }
                return;
            }
            const peer = getPeers().find(p => p.key === peerKey);
            if (peer !== undefined) {
                if (frontierLogEnabled()) {
                    console.debug('[hhs3:sync:frontier] push-back trigger', {
                        dagId: shortHash(dagId),
                        toPeer: peerKey,
                        localFrontier: frontierShort(localFrontier),
                        remoteFrontier: frontierShort(remoteFrontier),
                    });
                }
                sendFrontierTo(peer);
            }
        });
    }
    function frontiersEqual(a, b) {
        if (a.size !== b.size)
            return false;
        for (const h of a) {
            if (!b.has(h))
                return false;
        }
        return true;
    }
    function addToPeerDiscoveredFrontier(peerKey, hash) {
        let df = peerDiscoveredFrontier.get(peerKey);
        if (df === undefined) {
            df = new Set();
            peerDiscoveredFrontier.set(peerKey, df);
        }
        df.add(hash);
    }
    // --- core: attemptWork() ---
    function attemptWork() {
        return __awaiter(this, void 0, void 0, function* () {
            if (destroyed)
                return;
            if (workInProgress) {
                workNeeded = true;
                return;
            }
            workInProgress = true;
            workNeeded = true;
            while (workNeeded) {
                workNeeded = false;
                try {
                    yield attemptWorkOnce();
                }
                catch (_e) {
                    // prevent a single error from killing the work loop
                }
            }
            workInProgress = false;
        });
    }
    function attemptWorkOnce() {
        return __awaiter(this, void 0, void 0, function* () {
            // Step 1-2: issue header requests for unresolved prevs and unknown frontier hashes
            yield dispatchHeaderRequests();
            // Step 3: dispatch payload requests if there are discovered headers awaiting payloads
            if (discoveredHeaders.size > 0) {
                dispatchPayloads();
            }
            // Step 4-5: run validation loop, re-dispatch if progress was made
            if (readyToApply.size > 0) {
                const progress = yield runValidationLoop();
                if (progress) {
                    if (discoveredHeaders.size > 0) {
                        dispatchPayloads();
                    }
                    if (readyToApply.size > 0) {
                        const more = yield runValidationLoop();
                        if (more) {
                            workNeeded = true;
                        }
                    }
                }
            }
        });
    }
    // --- header dispatch ---
    function dispatchHeaderRequests() {
        return __awaiter(this, void 0, void 0, function* () {
            const localFrontier = yield dag.getFrontier();
            // Collect unresolved prevs: hashes referenced by discoveredHeaders but not in
            // discoveredHeaders, readyToApply, appliedEntries, or local DAG
            const unresolvedPrevs = new Set();
            for (const [_hash, header] of discoveredHeaders) {
                for (const prev of json.fromSet(header.prevEntryHashes)) {
                    if (!discoveredHeaders.has(prev) && !readyToApply.has(prev)
                        && !appliedEntries.has(prev) && !requestedPayloads.has(prev)) {
                        const local = yield dag.loadEntry(prev);
                        if (local === undefined) {
                            unresolvedPrevs.add(prev);
                        }
                    }
                }
            }
            // Also check peer frontiers for unknown hashes
            const unknownFrontierHashes = new Set();
            for (const [_peerKey, frontier] of peerFrontiers) {
                for (const h of frontier) {
                    if (!localFrontier.has(h) && !discoveredHeaders.has(h)
                        && !readyToApply.has(h) && !appliedEntries.has(h)) {
                        const local = yield dag.loadEntry(h);
                        if (local === undefined) {
                            unknownFrontierHashes.add(h);
                        }
                    }
                }
            }
            // Merge: everything we need to fetch headers for
            const needed = new Set([...unresolvedPrevs, ...unknownFrontierHashes]);
            if (needed.size === 0)
                return;
            // Filter out hashes that are already being fetched by an active header request
            const activelyFetching = new Set();
            for (const req of pendingHeaderRequests.values()) {
                for (const h of req.startHashes) {
                    activelyFetching.add(h);
                }
            }
            const peersWithSlots = getPeers();
            for (const peer of peersWithSlots) {
                const peerFrontier = peerFrontiers.get(peer.key);
                if (peerFrontier === undefined)
                    continue;
                // Already has a header request in flight to this peer? Skip.
                let hasActiveRequest = false;
                for (const req of pendingHeaderRequests.values()) {
                    if (req.peer.key === peer.key) {
                        hasActiveRequest = true;
                        break;
                    }
                }
                if (hasActiveRequest)
                    continue;
                // Collect hashes from this peer's frontier that we need
                const startHashes = [];
                for (const h of needed) {
                    if (!activelyFetching.has(h)) {
                        startHashes.push(h);
                    }
                }
                // Also add unknown frontier hashes that this peer specifically announced
                for (const h of peerFrontier) {
                    if (unknownFrontierHashes.has(h) && !activelyFetching.has(h)
                        && !startHashes.includes(h)) {
                        startHashes.push(h);
                    }
                }
                if (startHashes.length === 0)
                    continue;
                const useAutoPayload = autoPayloadRequestId === undefined;
                const requestId = newRequestId();
                const req = {
                    type: 'header-request',
                    requestId,
                    dagId,
                    start: startHashes,
                    limits: [...localFrontier],
                    maxHeaders: MAX_HEADERS_PER_REQUEST,
                    autoPayload: useAutoPayload,
                };
                const timeout = setTimeout(() => {
                    handleHeaderTimeout(requestId);
                }, REQUEST_TIMEOUT_MS);
                pendingHeaderRequests.set(requestId, {
                    requestId,
                    peer,
                    startHashes,
                    receivedHashes: new Set(),
                    expectedHeaderCount: undefined,
                    receivedHeaderCount: 0,
                    nextSequence: 0,
                    complete: false,
                    autoPayload: useAutoPayload,
                    expectedPayloadCount: undefined,
                    timeout,
                });
                if (useAutoPayload) {
                    autoPayloadRequestId = requestId;
                }
                for (const h of startHashes) {
                    activelyFetching.add(h);
                }
                const result = sendTo(peer, req);
                if (result !== 'sent') {
                    clearTimeout(timeout);
                    pendingHeaderRequests.delete(requestId);
                    if (autoPayloadRequestId === requestId) {
                        autoPayloadRequestId = undefined;
                    }
                    if (result === 'closed') {
                        removePeer(peer.key);
                    }
                }
            }
        });
    }
    // --- header response handling ---
    function handleHeaderResponseMeta(msg) {
        const state = pendingHeaderRequests.get(msg.requestId);
        if (state === undefined)
            return;
        if (msg.headerCount > MAX_HEADERS_PER_REQUEST) {
            failHeaderRequest(msg.requestId, 'headerCount exceeds maxHeaders');
            return;
        }
        if (msg.payloadCount > 0 && (!state.autoPayload || !msg.complete)) {
            failHeaderRequest(msg.requestId, 'payloadCount > 0 but autoPayload not set or not complete');
            return;
        }
        state.expectedHeaderCount = msg.headerCount;
        state.complete = msg.complete;
        state.expectedPayloadCount = msg.payloadCount > 0 ? msg.payloadCount : undefined;
        resetHeaderTimeout(state);
        if (msg.headerCount === 0 && msg.complete) {
            onHeaderRequestComplete(msg.requestId);
        }
    }
    function handleHeaderBatch(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            const state = pendingHeaderRequests.get(msg.requestId);
            if (state === undefined)
                return;
            if (msg.sequence !== state.nextSequence) {
                failHeaderRequest(msg.requestId, `expected sequence ${state.nextSequence}, got ${msg.sequence}`);
                return;
            }
            state.nextSequence++;
            if (state.expectedHeaderCount !== undefined &&
                state.receivedHeaderCount + msg.headers.length > state.expectedHeaderCount) {
                failHeaderRequest(msg.requestId, 'received more headers than announced');
                return;
            }
            for (const item of msg.headers) {
                const computed = hashSuite.hashToB64(stringToUint8Array(json.toStringNormalized(item.header)));
                if (computed !== item.hash) {
                    failHeaderRequest(msg.requestId, `hash mismatch for header: expected ${item.hash}, computed ${computed}`);
                    return;
                }
                state.receivedHashes.add(item.hash);
                // If we already have this entry locally (e.g. common ancestor
                // returned in a divergent-frontier response), do not queue it for
                // payload fetch/validation again.
                const localHeader = yield dag.loadHeader(item.hash);
                if (localHeader !== undefined) {
                    addToPeerDiscoveredFrontier(state.peer.key, item.hash);
                    continue;
                }
                // Skip duplicates (another peer may have sent the same header)
                if (!discoveredHeaders.has(item.hash) && !readyToApply.has(item.hash)
                    && !appliedEntries.has(item.hash)) {
                    discoveredHeaders.set(item.hash, item.header);
                }
            }
            state.receivedHeaderCount += msg.headers.length;
            resetHeaderTimeout(state);
            if (state.expectedHeaderCount !== undefined &&
                state.receivedHeaderCount >= state.expectedHeaderCount) {
                onHeaderRequestComplete(msg.requestId);
            }
            yield attemptWork();
        });
    }
    function onHeaderRequestComplete(requestId) {
        return __awaiter(this, void 0, void 0, function* () {
            const state = pendingHeaderRequests.get(requestId);
            if (state === undefined)
                return;
            clearTimeout(state.timeout);
            pendingHeaderRequests.delete(requestId);
            // Update peerDiscoveredFrontier for the serving peer
            for (const h of state.startHashes) {
                if (discoveredHeaders.has(h) || readyToApply.has(h) || appliedEntries.has(h)) {
                    addToPeerDiscoveredFrontier(state.peer.key, h);
                }
            }
            // Handle auto-payload transition: use receivedHashes (all hashes from this
            // request's batches) rather than discoveredHeaders.keys(), because some entries
            // may have already been applied by a concurrent auto-payload stream.
            if (state.autoPayload && state.expectedPayloadCount !== undefined
                && state.expectedPayloadCount > 0) {
                const autoHashes = state.receivedHashes;
                const timeout = setTimeout(() => {
                    handlePayloadTimeout(requestId);
                }, REQUEST_TIMEOUT_MS);
                pendingPayloadRequests.set(requestId, {
                    requestId,
                    peer: state.peer,
                    requestedHashes: autoHashes,
                    expectedPayloadCount: state.expectedPayloadCount,
                    receivedPayloadCount: 0,
                    nextSequence: 0,
                    timeout,
                });
                for (const h of autoHashes) {
                    requestedPayloads.add(h);
                }
            }
            if (autoPayloadRequestId === requestId) {
                autoPayloadRequestId = undefined;
            }
            yield attemptWork();
        });
    }
    function handleHeaderTimeout(requestId) {
        const state = pendingHeaderRequests.get(requestId);
        if (state === undefined)
            return;
        clearTimeout(state.timeout);
        pendingHeaderRequests.delete(requestId);
        reportIssue(state.peer.key, 'timeout');
        if (autoPayloadRequestId === requestId) {
            autoPayloadRequestId = undefined;
        }
        attemptWork();
    }
    function failHeaderRequest(requestId, _reason) {
        const state = pendingHeaderRequests.get(requestId);
        if (state === undefined)
            return;
        clearTimeout(state.timeout);
        pendingHeaderRequests.delete(requestId);
        reportIssue(state.peer.key, 'validation-failed');
        if (autoPayloadRequestId === requestId) {
            autoPayloadRequestId = undefined;
        }
        attemptWork();
    }
    // --- payload discovery and dispatch ---
    function findPayloadsForPeer(peerKey, maxCount) {
        const frontier = peerDiscoveredFrontier.get(peerKey);
        if (frontier === undefined)
            return [];
        const eligible = [];
        const visited = new Set();
        const queue = [...frontier];
        while (queue.length > 0) {
            const h = queue.pop();
            if (visited.has(h))
                continue;
            visited.add(h);
            const header = discoveredHeaders.get(h);
            if (header === undefined)
                continue;
            for (const prev of json.fromSet(header.prevEntryHashes)) {
                if (discoveredHeaders.has(prev) && !visited.has(prev)) {
                    queue.push(prev);
                }
            }
            if (requestedPayloads.has(h))
                continue;
            let ready = true;
            for (const prev of json.fromSet(header.prevEntryHashes)) {
                if (!appliedEntries.has(prev) && !receivedPayloads.has(prev)
                    && !requestedPayloads.has(prev)) {
                    const inDag = discoveredHeaders.has(prev) || readyToApply.has(prev);
                    if (inDag) {
                        ready = false;
                        break;
                    }
                    // If not in any discovered/ready map, it must be in the local DAG
                    // (otherwise it would be an unresolved prev and we'd still be fetching headers)
                }
            }
            if (ready)
                eligible.push(h);
        }
        return topoSort(eligible, discoveredHeaders).slice(0, maxCount);
    }
    function topoSort(hashes, headers) {
        const set = new Set(hashes);
        const order = [];
        const visited = new Set();
        function visit(h) {
            if (visited.has(h))
                return;
            if (!set.has(h))
                return;
            visited.add(h);
            const header = headers.get(h);
            if (header !== undefined) {
                for (const prev of json.fromSet(header.prevEntryHashes)) {
                    visit(prev);
                }
            }
            order.push(h);
        }
        for (const h of hashes) {
            visit(h);
        }
        return order;
    }
    function dispatchPayloads() {
        var _a, _b;
        const peers = getPeers();
        const peerInFlight = new Map();
        for (const ps of pendingPayloadRequests.values()) {
            const count = (_a = peerInFlight.get(ps.peer.key)) !== null && _a !== void 0 ? _a : 0;
            peerInFlight.set(ps.peer.key, count + 1);
        }
        for (const peer of peers) {
            const inflight = (_b = peerInFlight.get(peer.key)) !== null && _b !== void 0 ? _b : 0;
            if (inflight >= MAX_PAYLOAD_REQUESTS_PER_PEER)
                continue;
            const slotsAvailable = MAX_PAYLOAD_REQUESTS_PER_PEER - inflight;
            for (let i = 0; i < slotsAvailable; i++) {
                const chunk = findPayloadsForPeer(peer.key, PAYLOAD_CHUNK_SIZE);
                if (chunk.length === 0)
                    break;
                sendPayloadRequest(peer, chunk);
            }
        }
    }
    function sendPayloadRequest(peer, hashes) {
        const requestId = newRequestId();
        const req = {
            type: 'payload-request',
            requestId,
            dagId,
            hashes,
        };
        const timeout = setTimeout(() => {
            handlePayloadTimeout(requestId);
        }, REQUEST_TIMEOUT_MS);
        pendingPayloadRequests.set(requestId, {
            requestId,
            peer,
            requestedHashes: new Set(hashes),
            expectedPayloadCount: undefined,
            receivedPayloadCount: 0,
            nextSequence: 0,
            timeout,
        });
        for (const h of hashes) {
            requestedPayloads.add(h);
        }
        const result = sendTo(peer, req);
        if (result !== 'sent') {
            clearTimeout(timeout);
            pendingPayloadRequests.delete(requestId);
            for (const h of hashes) {
                requestedPayloads.delete(h);
            }
            if (result === 'closed') {
                removePeer(peer.key);
            }
        }
    }
    // --- payload response handling ---
    function handlePayloadResponseMeta(msg) {
        const state = pendingPayloadRequests.get(msg.requestId);
        if (state === undefined)
            return;
        if (msg.payloadCount !== state.requestedHashes.size) {
            failPayloadRequest(msg.requestId, 'payloadCount mismatch');
            return;
        }
        state.expectedPayloadCount = msg.payloadCount;
        resetPayloadTimeout(state);
    }
    function handlePayloadMsg(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const state = pendingPayloadRequests.get(msg.requestId);
            if (state === undefined)
                return;
            if (msg.sequence !== state.nextSequence) {
                failPayloadRequest(msg.requestId, `expected sequence ${state.nextSequence}, got ${msg.sequence}`);
                return;
            }
            state.nextSequence++;
            if (!state.requestedHashes.has(msg.hash)) {
                failPayloadRequest(msg.requestId, `received unrequested hash: ${msg.hash}`);
                return;
            }
            if (receivedPayloads.has(msg.hash) || appliedEntries.has(msg.hash)) {
                // Already have this payload (e.g. from a concurrent auto-payload stream).
                // Skip gracefully rather than failing the whole request.
                if (state.expectedPayloadCount !== undefined &&
                    state.receivedPayloadCount >= state.expectedPayloadCount) {
                    clearTimeout(state.timeout);
                    pendingPayloadRequests.delete(msg.requestId);
                }
                return;
            }
            if (state.expectedPayloadCount !== undefined &&
                state.receivedPayloadCount + 1 > state.expectedPayloadCount) {
                failPayloadRequest(msg.requestId, 'received more payloads than announced');
                return;
            }
            state.receivedPayloadCount++;
            hashSourcePeer.set(msg.hash, state.peer.key);
            resetPayloadTimeout(state);
            // Verify payload hash against header
            const header = (_a = discoveredHeaders.get(msg.hash)) !== null && _a !== void 0 ? _a : readyToApply.get(msg.hash);
            if (header !== undefined) {
                const computedPayloadHash = hashSuite.hashToB64(stringToUint8Array(json.toStringNormalized(msg.payload)));
                if (computedPayloadHash === header.payloadHash) {
                    // Hash verified: move from discoveredHeaders to readyToApply
                    discoveredHeaders.delete(msg.hash);
                    readyToApply.set(msg.hash, header);
                    receivedPayloads.set(msg.hash, msg.payload);
                    requestedPayloads.delete(msg.hash);
                }
                else {
                    reportIssue(state.peer.key, 'validation-failed');
                    requestedPayloads.delete(msg.hash);
                }
            }
            else {
                receivedPayloads.set(msg.hash, msg.payload);
                requestedPayloads.delete(msg.hash);
            }
            if (state.expectedPayloadCount !== undefined &&
                state.receivedPayloadCount >= state.expectedPayloadCount) {
                clearTimeout(state.timeout);
                pendingPayloadRequests.delete(msg.requestId);
            }
            yield attemptWork();
        });
    }
    function handlePayloadTimeout(requestId) {
        const state = pendingPayloadRequests.get(requestId);
        if (state === undefined)
            return;
        clearTimeout(state.timeout);
        pendingPayloadRequests.delete(requestId);
        reportIssue(state.peer.key, 'timeout');
        for (const h of state.requestedHashes) {
            if (!receivedPayloads.has(h) && !appliedEntries.has(h)) {
                requestedPayloads.delete(h);
            }
        }
        attemptWork();
    }
    function failPayloadRequest(requestId, _reason) {
        const state = pendingPayloadRequests.get(requestId);
        if (state === undefined)
            return;
        clearTimeout(state.timeout);
        pendingPayloadRequests.delete(requestId);
        reportIssue(state.peer.key, 'validation-failed');
        for (const h of state.requestedHashes) {
            if (!receivedPayloads.has(h) && !appliedEntries.has(h)) {
                requestedPayloads.delete(h);
            }
        }
        attemptWork();
    }
    // --- validation / import loop ---
    function runValidationLoop() {
        return __awaiter(this, void 0, void 0, function* () {
            const order = topoSort([...readyToApply.keys()], readyToApply);
            let anyProgress = false;
            let progress = true;
            while (progress) {
                progress = false;
                for (const hash of order) {
                    if (appliedEntries.has(hash))
                        continue;
                    const payload = receivedPayloads.get(hash);
                    if (payload === undefined)
                        continue;
                    const header = readyToApply.get(hash);
                    if (header === undefined)
                        continue;
                    const prevHashes = [...json.fromSet(header.prevEntryHashes)];
                    let allPredecessorsReady = true;
                    for (const prev of prevHashes) {
                        if (!appliedEntries.has(prev)) {
                            const localEntry = yield dag.loadEntry(prev);
                            if (localEntry === undefined) {
                                allPredecessorsReady = false;
                                break;
                            }
                        }
                    }
                    if (!allPredecessorsReady)
                        continue;
                    const version = new Set(prevHashes);
                    const foreignDeps = rObject.extractForeignDeps(payload, version);
                    if (foreignDeps !== undefined && resolveRefDag !== undefined) {
                        let allDepsAvailable = true;
                        for (const dep of foreignDeps) {
                            const refDag = yield resolveRefDag(dep.dagId);
                            if (refDag === undefined) {
                                allDepsAvailable = false;
                                break;
                            }
                            for (const requiredHash of dep.requiredHashes) {
                                if ((yield refDag.loadHeader(requiredHash)) === undefined) {
                                    allDepsAvailable = false;
                                    break;
                                }
                            }
                            if (!allDepsAvailable)
                                break;
                        }
                        if (!allDepsAvailable)
                            continue;
                    }
                    const result = yield rObject.validatePayload(payload, version);
                    if (!result.valid) {
                        const source = hashSourcePeer.get(hash);
                        if (source !== undefined)
                            reportIssue(source, 'validation-failed');
                        receivedPayloads.delete(hash);
                        readyToApply.delete(hash);
                        discardDependents(hash);
                        continue;
                    }
                    const resultHash = yield rObject.applyPayload(payload, version);
                    if (resultHash !== hash) {
                        const source = hashSourcePeer.get(hash);
                        if (source !== undefined)
                            reportIssue(source, 'validation-failed');
                        receivedPayloads.delete(hash);
                        readyToApply.delete(hash);
                        discardDependents(hash);
                        continue;
                    }
                    appliedEntries.add(hash);
                    receivedPayloads.delete(hash);
                    readyToApply.delete(hash);
                    progress = true;
                    anyProgress = true;
                }
            }
            return anyProgress;
        });
    }
    function discardDependents(badHash) {
        const discarded = new Set();
        discarded.add(badHash);
        const allHeaders = new Map([...discoveredHeaders, ...readyToApply]);
        const order = topoSort([...allHeaders.keys()], allHeaders);
        for (const hash of order) {
            if (discarded.has(hash))
                continue;
            const header = allHeaders.get(hash);
            if (header === undefined)
                continue;
            for (const prev of json.fromSet(header.prevEntryHashes)) {
                if (discarded.has(prev)) {
                    discarded.add(hash);
                    receivedPayloads.delete(hash);
                    discoveredHeaders.delete(hash);
                    readyToApply.delete(hash);
                    requestedPayloads.delete(hash);
                    break;
                }
            }
        }
    }
    // --- helpers ---
    function resetHeaderTimeout(state) {
        clearTimeout(state.timeout);
        state.timeout = setTimeout(() => {
            handleHeaderTimeout(state.requestId);
        }, REQUEST_TIMEOUT_MS);
    }
    function resetPayloadTimeout(state) {
        clearTimeout(state.timeout);
        state.timeout = setTimeout(() => {
            handlePayloadTimeout(state.requestId);
        }, REQUEST_TIMEOUT_MS);
    }
    // --- message handling ---
    function handleMessage(msg, channel) {
        if (destroyed)
            return;
        const pk = `${channel.peerId}@${channel.endpoint}`;
        switch (msg.type) {
            case 'new-frontier':
                if (msg.dagId === dagId)
                    handleNewFrontier(msg, pk);
                break;
            case 'header-response-meta':
                handleHeaderResponseMeta(msg);
                break;
            case 'header-batch':
                handleHeaderBatch(msg);
                break;
            case 'payload-response-meta':
                handlePayloadResponseMeta(msg);
                break;
            case 'payload-msg':
                handlePayloadMsg(msg);
                break;
            default:
                break;
        }
    }
    function addPeer(_peer) {
        broadcastFrontier();
        attemptWork();
    }
    function removePeer(peerKey) {
        peerFrontiers.delete(peerKey);
        peerDiscoveredFrontier.delete(peerKey);
        lastSentFrontier.delete(peerKey);
        for (const [rid, state] of pendingHeaderRequests) {
            if (state.peer.key === peerKey) {
                clearTimeout(state.timeout);
                pendingHeaderRequests.delete(rid);
                if (autoPayloadRequestId === rid) {
                    autoPayloadRequestId = undefined;
                }
            }
        }
        for (const [rid, state] of pendingPayloadRequests) {
            if (state.peer.key === peerKey) {
                clearTimeout(state.timeout);
                pendingPayloadRequests.delete(rid);
                for (const h of state.requestedHashes) {
                    if (!receivedPayloads.has(h) && !appliedEntries.has(h)) {
                        requestedPayloads.delete(h);
                    }
                }
            }
        }
        attemptWork();
    }
    function destroy() {
        destroyed = true;
        dag.removeListener(onGrowth);
        for (const state of pendingHeaderRequests.values()) {
            clearTimeout(state.timeout);
        }
        pendingHeaderRequests.clear();
        for (const state of pendingPayloadRequests.values()) {
            clearTimeout(state.timeout);
        }
        pendingPayloadRequests.clear();
    }
    function onPeerIssue(cb) {
        issueCallbacks.push(cb);
    }
    function getDiagnostics() {
        return {
            pendingHeaderRequests: pendingHeaderRequests.size,
            pendingPayloadRequests: pendingPayloadRequests.size,
        };
    }
    return { handleMessage, addPeer, removePeer, broadcastFrontier, destroy, onPeerIssue, getDiagnostics };
}
